/**
 * Full-page Field Operation editor — replaces the popup that previously
 * lived inside FieldOperations.jsx. Used at:
 *   /field-operations/new
 *   /field-operations/:opId/edit
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarClock, ArrowLeft, Loader2, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

const TOKEN_KEY = 'nexus_token';
const apiCall = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const emptyForm = {
  case_id: '', case_number: '', case_name: '', client_name: '',
  operation_title: '', operation_type: 'SURVEILLANCE',
  assigned_investigator_ids: [], lead_investigator_id: '',
  date: '', start_time: '09:00', end_time: '17:00',
  estimated_hours: 8,
  operation_location: '', meeting_location: '',
  task_instructions: '', operational_notes: '', equipment_needed: '',
  priority: 'NORMAL',
  pay_type: 'HOURLY', hourly_rate: 45, flat_rate: 0,
};

export default function FieldOperationForm() {
  const { opId } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(opId);
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ types: [], priorities: [] });
  const [investigators, setInvestigators] = useState([]);
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);

  const load = useCallback(async () => {
    try {
      const [metaRes, usersRes, casesRes] = await Promise.all([
        apiCall('/api/field-operations/meta/options').catch(() => ({ types: [], priorities: [] })),
        apiCall('/api/users').catch(() => ([])),
        apiCall('/api/cases').catch(() => ([])),
      ]);
      setMeta(metaRes);
      // /api/users returns a plain array of user documents; keep only
      // investigator-type roles that are active. "active" = any user with an
      // investigator role (field_investigator, skip_tracer, transporter).
      const raw = Array.isArray(usersRes) ? usersRes : (usersRes.users || []);
      const invRoles = new Set(['field_investigator', 'skip_tracer', 'transporter']);
      const active = raw.filter(u =>
        invRoles.has(u.role) &&
        (u.status !== 'DISABLED' && u.status !== 'DELETED' && u.is_active !== false)
      );
      setInvestigators(active);
      setCases(Array.isArray(casesRes) ? casesRes : (casesRes.cases || []));
      if (editing) {
        const op = await apiCall(`/api/field-operations/${opId}`);
        setForm({ ...emptyForm, ...op });
      }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [editing, opId]);
  useEffect(() => { load(); }, [load]);

  const estimatedPay = (() => {
    if (form.pay_type === 'FLAT') return Number(form.flat_rate || 0);
    return Math.round(Number(form.estimated_hours || 0) * Number(form.hourly_rate || 0) * 100) / 100;
  })();

  const save = async () => {
    if (!form.operation_title || !form.date || !form.start_time) {
      toast.error('Title, Date, and Start Time are required'); return;
    }
    if (!form.case_id) {
      toast.error('Case is required — select a case from the dropdown'); return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.estimated_hours = Number(payload.estimated_hours || 0);
      payload.hourly_rate = Number(payload.hourly_rate || 0);
      payload.flat_rate = Number(payload.flat_rate || 0);
      if (editing) {
        await apiCall(`/api/field-operations/${opId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        toast.success('Operation updated');
      } else {
        await apiCall('/api/field-operations', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Operation scheduled');
      }
      navigate('/field-operations');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-slate-300"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="fops-form-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/field-operations')} variant="outline" className="border-slate-600 !text-slate-200" data-testid="fops-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <CalendarClock className="w-6 h-6 text-amber-300" />
              {editing ? 'Edit Field Operation' : 'Schedule New Field Operation'}
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">Investigators are notified the moment you save.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 bg-[#131A2A] border border-slate-700 rounded-lg p-5">
        <div>
          <Label className="text-slate-300">Operation Title *</Label>
          <Input value={form.operation_title} onChange={e => setForm(f => ({ ...f, operation_title: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-title" />
        </div>
        <div>
          <Label className="text-slate-300">Operation Type</Label>
          <Select value={form.operation_type} onValueChange={v => setForm(f => ({ ...f, operation_type: v }))}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta.types || []).map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Case *</Label>
          <Select
            value={form.case_id || ''}
            onValueChange={v => {
              const c = cases.find(x => x.id === v);
              setForm(f => ({
                ...f,
                case_id: v,
                case_number: c?.case_number || f.case_number,
                case_name: c?.title || f.case_name,
                client_name: c?.client_name || f.client_name,
              }));
            }}
          >
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-case"><SelectValue placeholder="Select case" /></SelectTrigger>
            <SelectContent>
              {cases.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500 italic">No cases found.</div>
              )}
              {cases.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Case Number</Label>
          <Input value={form.case_number} readOnly
            className="bg-slate-900 border-slate-700 text-slate-400" data-testid="fops-form-case-number" />
        </div>
        <div>
          <Label className="text-slate-300">Client Name</Label>
          <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-client" />
        </div>
        <div>
          <Label className="text-slate-300">Date *</Label>
          <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-date" />
        </div>
        <div>
          <Label className="text-slate-300">Priority</Label>
          <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta.priorities || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Start Time *</Label>
          <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-start" />
        </div>
        <div>
          <Label className="text-slate-300">End Time</Label>
          <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-end" />
        </div>
        <div>
          <Label className="text-slate-300">Operation Location</Label>
          <Input value={form.operation_location} onChange={e => setForm(f => ({ ...f, operation_location: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-location" />
        </div>
        <div>
          <Label className="text-slate-300">Meeting Location</Label>
          <Input value={form.meeting_location} onChange={e => setForm(f => ({ ...f, meeting_location: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-meeting" />
        </div>
        <div className="col-span-1 lg:col-span-2">
          <Label className="text-slate-300">Assigned Investigators</Label>
          <Select value="" onValueChange={(v) => {
            if (!v) return;
            setForm(f => f.assigned_investigator_ids?.includes(v) ? f : { ...f, assigned_investigator_ids: [...(f.assigned_investigator_ids || []), v] });
          }}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-add-inv"><SelectValue placeholder="Add investigator…" /></SelectTrigger>
            <SelectContent>
              {investigators.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500 italic">No active investigators found.</div>
              )}
              {investigators.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name || u.full_name || u.username || u.id}
                  {u.role && <span className="text-slate-400 ml-1.5 text-[10px]">· {u.role.replace(/_/g,' ')}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(form.assigned_investigator_ids || []).map(id => {
              const u = investigators.find(x => x.id === id);
              return (
                <Badge key={id} className="bg-amber-500/20 border border-amber-500/40 !text-amber-200 inline-flex items-center gap-1.5 py-1 px-2 text-xs font-normal" data-testid={`fops-inv-badge-${id}`}>
                  <span>{u ? (u.name || u.full_name || u.username) : id}</span>
                  <button type="button" className="text-amber-300/70 hover:text-amber-100 leading-none text-sm" onClick={() => setForm(f => ({ ...f, assigned_investigator_ids: f.assigned_investigator_ids.filter(x => x !== id) }))} data-testid={`fops-inv-remove-${id}`}>×</button>
                </Badge>
              );
            })}
          </div>
        </div>
        <div className="col-span-1 lg:col-span-2">
          <Label className="text-slate-300">Task Instructions</Label>
          <Textarea rows={4} value={form.task_instructions} onChange={e => setForm(f => ({ ...f, task_instructions: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-instructions" />
        </div>
        <div>
          <Label className="text-slate-300">Equipment Needed</Label>
          <Input value={form.equipment_needed} onChange={e => setForm(f => ({ ...f, equipment_needed: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-equipment" />
        </div>
        <div>
          <Label className="text-slate-300">Operational Notes</Label>
          <Input value={form.operational_notes} onChange={e => setForm(f => ({ ...f, operational_notes: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-notes" />
        </div>
      </div>

      {/* Pay block */}
      <div className="bg-emerald-500/10 border-2 border-emerald-500/40 rounded p-4" data-testid="fops-form-pay">
        <h3 className="font-bold text-emerald-200 flex items-center gap-1.5 mb-3"><DollarSign className="w-5 h-5" /> Pay</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-slate-300">Pay Type</Label>
            <Select value={form.pay_type} onValueChange={v => setForm(f => ({ ...f, pay_type: v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-pay-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="HOURLY">Hourly</SelectItem>
                <SelectItem value="FLAT">Flat Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.pay_type === 'HOURLY' ? (
            <>
              <div>
                <Label className="text-slate-300">Hourly Rate ($)</Label>
                <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-hourly" />
              </div>
              <div>
                <Label className="text-slate-300">Estimated Hours</Label>
                <Input type="number" min="0" step="0.25" value={form.estimated_hours} onChange={e => setForm(f => ({ ...f, estimated_hours: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-hours" />
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <Label className="text-slate-300">Flat Rate ($)</Label>
              <Input type="number" min="0" step="0.01" value={form.flat_rate} onChange={e => setForm(f => ({ ...f, flat_rate: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white" data-testid="fops-form-flat" />
            </div>
          )}
          <div className="bg-emerald-500/20 rounded px-3 py-2">
            <p className="text-[10px] text-emerald-200 font-mono uppercase tracking-wider">Estimated Total</p>
            <p className="text-2xl font-extrabold text-emerald-100" data-testid="fops-form-total">${estimatedPay.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="fops-action-bar">
        <Button onClick={() => navigate('/field-operations')} variant="outline" className="border-slate-500 !text-slate-200" data-testid="fops-cancel">Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="fops-form-save">
          {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : (editing ? 'Save Changes' : 'Schedule Operation')}
        </Button>
      </div>
    </div>
  );
}
